// React and hooks
import React, { useState, useEffect } from 'react';
// Redux
import { useDispatch } from 'react-redux';
// Redux Actions
import { setCurrentUser, setIsLoginUser } from 'slices/auth/authSlice';
// React Router
import { NavLink } from 'react-router-dom';
// UI Components
import { Row, Col, Card, Button, Alert } from 'react-bootstrap';
// External Libraries
import { motion } from 'framer-motion';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
// API Calls
import { getVerificationStatus } from 'api/Api-user';
// utils
import { handlerErrors } from 'utils/handleErrors';
// Custom Components
import Loading from '../../../components/loading/LoadingRed';
import ShippingRefundModal from '../../../components/modals/ShippingRefundModal';
import VideoModal from '../components/VideoModal';
import ShipmentCard from '../components/ShipmentCard';
import DashboardCard from '../../../components/cards/DashboardCard';
import ReportModal from '../../../components/modals/ReportModal';
import NotificationGallery from '../../../components/carousel/NotificationsGallery';
// import InfoModal from '../../../components/modals/InfoModal';

function AuthorizedUserPanel({ isUserAllowed = false, lastShippings = [], notifications = [], isLoading = false, setUpdatePage }) {
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const dispatch = useDispatch();
  const cardVariants = {
    hidden: { opacity: 0, y: 50 }, // Empieza invisible y desplazado hacia abajo
    visible: { opacity: 1, y: 0 }, // Se vuelve visible y se posiciona
  };
  const status = {
    PROFILE_INFO: {
      step: 'PROFILE_INFO',
      number: 0,
      title: 'Verifica tu cuenta',
      message: 'Aún no has iniciado tu verificación. Empieza ahora para poder realizar envíos.',
      color: 'warning',
      icon: 'clock',
      img: 'documentation',
    },
    IDENTITY_VERIFICATION: {
      step: 'IDENTITY_VERIFICATION',
      number: 1,
      title: 'Verificación incompleta',
      message: 'Tu identidad aún no está verificada. Continúa con los siguientes pasos para activar tu cuenta.',
      color: 'info',
      icon: 'clock',
      img: 'analytics',
    },
    BILLING_INFO: {
      step: 'BILLING_INFO',
      number: 2,
      title: 'Último paso pendiente',
      message: 'Solo falta confirmar si necesitas facturas. Completa este paso para activar tu cuenta.',
      color: 'danger',
      icon: 'close-circle',
      img: 'documentation',
    },
    COMPLETED: {
      step: ' COMPLETED',
      number: 3,
      title: 'Verificación completa',
      message: 'Tu cuenta ya está verificada. Inicia sesión nuevamente para comenzar a disfrutar de la plataforma.',
      color: 'success',
      icon: 'check-circle',
      img: 'performance',
    },
  };
  const [accountStatus, setAccountStatus] = useState(status[localStorage.getItem('onboardingStep') || 'PROFILE_INFO']);
  // console.log('estado de la centa: ', accountStatus);
  const logout = () => {
    // history.push('/login');
    dispatch(setCurrentUser({}));
    dispatch(setIsLoginUser(false));
  };
  const handlerVerificationStatus = async () => {
    try {
      const response = await getVerificationStatus();
      // console.log('response handlerVerificationStatus:', response);
      if (response.success !== undefined) {
        localStorage.setItem('onboardingStep', response.onboarding_step);
        const step = status[response.onboarding_step];
        if (step !== undefined) {
          setAccountStatus(step);
        }
      }
    } catch (error) {
      handlerErrors(error);
    }
  };

  useEffect(() => {
    // just if user is unverified
    if (!isUserAllowed) {
      handlerVerificationStatus();
    }
  }, []);

  return (
    <>
      <ShippingRefundModal setIsRefundModalOpen={setIsRefundModalOpen} isRefundModalOpen={isRefundModalOpen} setUpdatePage={setUpdatePage} />
      <ReportModal isModalOpen={isReportModalOpen} setIsModalOpen={setIsReportModalOpen} />
      {/* Warning Modal */}
      {/* <InfoModal /> */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="YSbmX0oNCUU" // ID del video de YouTube
      />
      <Row className="bg-warningx g-0 d-flex align-items-center">
        {/* Last Shippings Start */}
        {/* si el usuario esta verificado y la lista esta vacia el loader se pone en true && isUserAllowed  */}
        {isLoading && (
          <Col lg="6" xxl="7" className="mb-5 order-2 order-lg-1 bg-infox">
            <h2 className="small-title d-none">Últimos Envíos Realizados</h2>
            <div style={{ minHeight: '150px' }}>
              {isLoading && (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '150px' }}>
                  <Loading />
                </div>
              )}
            </div>
          </Col>
        )}
        {/* si el usuario esta verificado y el array no esta cargando ni esta vacio isUserAllowed &&  */}
        {lastShippings?.length > 0 && (
          <Col lg="6" xxl="7" className="mb-5 order-2 order-lg-1 bg-successx">
            <h2 className="small-title">Últimos Envíos Realizados</h2>
            {lastShippings.map((shipping, index) => (
              <motion.div
                key={index}
                initial="hidden"
                animate="visible"
                transition={{
                  duration: 0.5,
                  delay: index * 0.2, // Retraso para animación secuencial
                }}
                variants={cardVariants}
              >
                <ShipmentCard shipping={shipping} />
              </motion.div>
            ))}
          </Col>
        )}
        {/* si el usuario esta verificado pero su lista esta vacia o sea no ha hecho su primer envio */}
        {lastShippings?.length === 0 && (
          <Col lg="6" xxl="7" className="mb-5 order-2 order-lg-1 bg-infox">
            <h2 className="small-title">Últimos Envíos Realizados</h2>
            <Card className="sh-40 h-xl-100-card">
              {/* && lastShippings?.length === 0 */}
              <Card.Body className="d-flex align-items-center justify-content-center h-100">
                <div className="text-center">
                  <img src="/img/illustration/icon-storage.webp" className="theme-filter mb-1" alt="launch" />
                  <p className={`mb-3 text-primary ${isUserAllowed ? 'visible' : 'invisible'}`}>Crea tu primer envio!</p>
                  <p className="mb-3">Aquí encontrarás un listado de tus últimos envíos.</p>
                  <div className={isUserAllowed ? 'visible' : 'invisible'}>
                    <NavLink to="/envios/enviar" className="btn btn-primary" id="goFormFirstTime">
                      <CsLineIcons icon="arrow-double-left" size="15" className="me-1" />
                      <span>Crear envío</span>
                    </NavLink>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
        {/* si el usuario no esta verificado */}
        {!isUserAllowed && (
          <Row className="g-0 bg-infox d-flex align-items-start">
            <Col lg="6" className="mb-5 d-flex flex-column justify-content-center order-2 order-lg-1 bg-infox">
              <Card className="sh-auto sh-lg-50 ">
                <Card.Body className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center">
                    <img src="/img/illustration/icon-launch.webp" className="theme-filter mb-1" alt="launch" />
                    <p className="mb-3 text-primary">¡Gracias por registrarte con nosotros!</p>
                    <p className="mb-3">Tu cuenta está en proceso de activación. Pronto podrás acceder a nuestros servicios.</p>
                    <p className="mb-3">Si tienes alguna duda o necesitas asistencia, contactanos.</p>
                    <div>
                      <Button
                        className="btn btn-primary"
                        onClick={() =>
                          window.open(
                            `https://wa.me/+523319774057/?text=${`Hola, acabo de registrarme y me gustaría saber el estado de la activación de mi cuenta.`}`
                          )
                        }
                      >
                        <span>Contactar por whatsapp</span>
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg="6" className="mb-5 ps-2 d-flex flex-column justify-content-center order-1 order-lg-2 bg-successx">
              <Card className="sh-auto sh-lg-50  position-relative">
                <Card.Body className="d-flex flex-column align-items-center justify-content-center h-100">
                  <div className="bg-infox d-flex justify-content-end position-absolute" style={{ top: '20px', right: '0px' }}>
                    <NavLink to="/verificacion">
                      <Alert variant={accountStatus.color} className="d-flex">
                        <CsLineIcons icon={accountStatus.icon} className="me-1" />
                        <span>{accountStatus.title}</span>
                      </Alert>
                    </NavLink>
                  </div>
                  <div className="text-center">
                    <img src={`/img/illustration/icon-${accountStatus.img}.webp`} className="theme-filter mb-1" alt="launch" width="150px" />
                    <p className="mb-3 text-primary">{accountStatus.title}</p>
                    <p className="mb-3">{accountStatus.message}</p>
                    {accountStatus?.step !== 'COMPLETED' && (
                      <div>
                        <NavLink to="/verificacion" className="btn btn-primary mt-5">
                          <span>Verificar cuenta</span>
                        </NavLink>
                      </div>
                    )}
                    {accountStatus.step === 'COMPLETED' && (
                      <div>
                        <Button onClick={logout}>
                          <CsLineIcons icon="rotate-right" size="15" className="me-1" />
                          <span>Iniciar sesión</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
        {/* Last Shippings End */}
        {/* Notifications Start */}
        {isUserAllowed && (
          <Col sm="8" lg="6" xxl="5" className="mx-auto mb-5 ps-lg-3 pt-lg-3 order-1 order-lg-2 bg-successx">
            <h2 className="small-title d-none">Notificaciones</h2>
            <div className=" bg-infox" style={{ maxWidth: '746px' }}>
              <div style={{ minHeight: '150px' }} className={`bg-infox ${notifications.length === 0 ? 'd-none' : 'd-block'}`}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }} // Empieza transparente y desplazado hacia abajo
                  animate={{ opacity: 1, y: 0 }} // Se muestra y vuelve a su posición
                  transition={{ duration: 0.5, ease: 'easeOut' }} // Transición suave
                >
                  {notifications.length > 0 && <NotificationGallery notifications={notifications} />}
                </motion.div>
              </div>
            </div>
          </Col>
        )}
        {/* Notifications End */}
      </Row>
      {/* Cards Start */}
      <Row className="bg-infox">
        <Col xs="12" sm="6" xl="3">
          <Card className="h-100-card hover-img-scale-up mx-auto mb-3" style={{ minHeight: '300px', maxWidth: '480px', backgroundColor: '#f1f1f1' }}>
            <img src="/img/autopaquete/enviar/manual.webp" className="card-img h-100 scale" alt="card image" />
            <Card.Body className="d-flex flex-column justify-content-between align-items-start  bg-transparent card-img-overlay">
              <div>
                <div className="cta-3">¿Nuevo por aquí?</div>
                <div className="mb-3 cta-3 text-primary">Introducción</div>
                <div className="text-alternate mb-4">Descubre cómo usar nuestra plataforma con este breve video.</div>
              </div>
              <Button
                variant="outline-primary"
                id="initVideo"
                className="w-auto btn btn-icon  btn-icon-start ms-0 ms-sm-1"
                onClick={() => {
                  setIsVideoModalOpen(true);
                }}
              >
                <CsLineIcons icon="arrow-right" />
                <span className="ps-2">Ver Video</span>
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <DashboardCard
          img="/img/autopaquete/enviar/recarga.webp"
          title="¿Tienes dudas?"
          subTitle="Manual de usuario"
          description="Consulta nuestra guía de preguntas frecuentes para resolver todas tus dudas."
          url="https://autopaquete-log.glide.page/dl/d0a5f4"
          btnTitle="Ver manual"
        />
        <Col xs="12" sm="6" xl="3">
          <Card className="h-100-card hover-img-scale-up mx-auto mb-3" style={{ minHeight: '300px', maxWidth: '480px', backgroundColor: '#f1f1f1' }}>
            <img src="/img/autopaquete/enviar/soporte.webp" className="card-img h-100 scale" alt="card image" />
            <Card.Body className="d-flex flex-column justify-content-between align-items-start  bg-transparent card-img-overlay">
              <div>
                <div className="cta-3">¿Tu paquete tiene incidencia?</div>
                <div className="mb-3 cta-3 text-primary">Realizar reporte</div>
                <div className="text-alternate mb-4">Proporciónanos los datos de tu paquete y nosotros nos encargamos del reporte.</div>
              </div>
              <Button
                variant="outline-primary"
                data-gtm="init-report"
                className="w-auto btn btn-icon btn-icon-start ms-0 ms-sm-1"
                onClick={() => {
                  setIsReportModalOpen(true);
                }}
              >
                <CsLineIcons icon="arrow-right" />
                <span className="ps-2">Nuevo Reporte</span>
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col xs="12" sm="6" xl="3">
          <Card className="h-100-card hover-img-scale-up mx-auto mb-3" style={{ minHeight: '300px', maxWidth: '480px', backgroundColor: '#f1f1f1' }}>
            <img src="/img/autopaquete/enviar/reembolso.webp" className="card-img h-100 scale" alt="card image" />
            <Card.Body className="d-flex flex-column justify-content-between align-items-start  bg-transparent card-img-overlay">
              <div>
                <div className="cta-3">¿Guía sin usar?</div>
                <div className="mb-3 cta-3 text-primary">Reembolso de guías</div>
                <div className="text-alternate mb-4">Facilitamos tu reembolso de guías con un proceso simple y sin complicaciones.</div>
              </div>
              <Button
                variant="outline-primary"
                data-gtm="init-cancellations"
                className="w-auto btn btn-icon btn-icon-start ms-0 ms-sm-1"
                onClick={() => {
                  setIsRefundModalOpen(true);
                }}
              >
                <CsLineIcons icon="arrow-right" />
                <span className="ps-2">Solicitar reembolso</span>
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Cards End */}
    </>
  );
}

export default AuthorizedUserPanel;
